/**
 * The Orcaloops Privacy Policy page body, rendered by the public
 * `orcaloops.ai/privacy` route (`routes/orca/privacy.tsx`). Kept out of the route
 * module so tests render it directly instead of reaching through the Route
 * object.
 *
 * @since 1.0.0
 */
import type { ReactNode } from "react";

import {
  orcaLegalLinkClass,
  orcaLegalListClass,
  OrcaLegalPageLayout,
  OrcaLegalSection,
} from "~/components/orca/OrcaLegalPageLayout.tsx";

/** The support mailbox, rendered as a mailto link. */
const supportEmail = <a href="mailto:support@consinna.ai" className={orcaLegalLinkClass}>support@consinna.ai</a>;

export function OrcaPrivacyPage(): ReactNode {
  return (
    <OrcaLegalPageLayout
      title="Orcaloops Privacy Policy"
      lastUpdated="12 September 2026"
      version="1.0"
      current="/orca/privacy"
      testId="orca-privacy-page"
    >
      <OrcaLegalSection title="1. Who we are">
        <p>
          Orcaloops is operated by Consinna LLC, a California limited liability company. Contact: {supportEmail}.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="2. What this policy covers">
        <p>
          This policy covers the orcaloops.ai web application and the orchestration service behind it. It does not cover
          third-party services you connect — GitHub, Linear, your LLM provider — which are governed by their own
          policies.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="3. Data we collect">
        <ul className={orcaLegalListClass}>
          <li>
            <strong>Account data.</strong>{" "}
            Your name and email address, handled through our authentication provider Clerk, including the profile Google
            returns when you sign in with Google.
          </li>
          <li>
            <strong>Connected-service data.</strong>{" "}
            Repository metadata and content reachable through the GitHub App installation <strong>you</strong>{" "}
            authorize on <strong>your</strong> repositories, and issue and project data from the Linear workspace{" "}
            <strong>you</strong> connect.
          </li>
          <li>
            <strong>Run data.</strong>{" "}
            Task transcripts, logs, diffs and artifacts produced by runs you start, stored so you can review the
            evidence for each run.
          </li>
          <li>
            <strong>Operational data.</strong>{" "}
            Server logs, IP address and request metadata needed to run and secure the service.
          </li>
        </ul>
      </OrcaLegalSection>

      <OrcaLegalSection title="4. Google sign-in and Google user data">
        <p>
          When you sign in with Google we request only the <code>openid</code>, <code>email</code> and{" "}
          <code>profile</code>{" "}
          scopes. We use the identifier, email address and basic profile Google returns solely to create and
          authenticate your Orcaloops account. We request no Google Workspace, Drive, Gmail or Calendar scopes.
        </p>
        <p>
          {"Orcaloops' use and transfer of information received from Google APIs to any other app will adhere to the "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className={orcaLegalLinkClass}
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="5. How we use data">
        <p>
          We use your data to authenticate you, to execute the orchestration runs you request, to show you run history
          and evidence, to bill you, to support you, and to secure and debug the service. Nothing else.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="6. We do not sell your data">
        <p>
          We do not sell, rent or trade personal information, and we do not use your repository content or run data to
          train models for our own or any third party&rsquo;s benefit.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="7. Sharing">
        <p>
          We share data only with service providers acting on our instructions to operate the product — authentication,
          cloud hosting, the LLM providers that execute the runs you start, and payment processing — and where required
          by law.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="8. Retention">
        <p>
          We keep account data for the life of the account; run transcripts and artifacts until you delete them or the
          account closes; and operational logs on a rolling short-term basis.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="9. Security">
        <p>
          Credentials and connection tokens are stored encrypted, and access to production data is limited to personnel
          who need it.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="10. Your choices and rights">
        <p>
          You can disconnect GitHub or Linear at any time, revoke Orcaloops&rsquo; access from your Google Account
          security settings, and request access to or deletion of your account data by emailing {supportEmail}.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="11. Children">
        <p>The service is not directed to anyone under 16.</p>
      </OrcaLegalSection>

      <OrcaLegalSection title="12. Changes">
        <p>
          We will update the &ldquo;last updated&rdquo; date above and, for material changes, notify account holders.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="13. Contact">
        <p>Questions about this policy: {supportEmail}.</p>
      </OrcaLegalSection>
    </OrcaLegalPageLayout>
  );
}
